import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Nav, Footer } from "../components/Nav";
import { toast } from "sonner";

export default function Enroll() {
  const { user, loading: authLoading } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [currency, setCurrency] = useState("inr");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (!tz.includes("Kolkata") && !tz.includes("Calcutta")) setCurrency("usd");
    } catch { /* noop */ }
  }, []);

  // Route guard — send unauthenticated users to signup, admin to admin page
  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/signup?next=/enroll"); return; }
    if (user.role === "admin") { nav("/admin"); return; }
  }, [user, authLoading, nav]);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const startPayment = async () => {
    if (!user) { nav("/signup?next=/enroll"); return; }
    setLoading(true);
    try {
      if (currency === "inr") {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Failed to load Razorpay");
        const { data } = await api.post("/payments/razorpay/create-order", {
          course_id: "indian_fashion_illustration",
        });
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: "Lata Illustrator",
          description: "Indian Fashion Illustration — The Complete Course",
          prefill: { name: user.name, email: user.email },
          theme: { color: "#D4AF37" },
          handler: async (resp) => {
            try {
              const { data } = await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              // Only redirect if backend explicitly returned {status:"ok"}.
              // Any other shape (or a thrown error) means "do not grant access".
              if (data?.status === "ok") {
                window.location.href = `/payment/success?session_id=${resp.razorpay_order_id}`;
              } else {
                toast.error("Payment could not be verified. No access granted.");
                setLoading(false);
              }
            } catch (e) {
              // Backend rejected the payment (bad signature, not captured, wrong amount, etc.)
              toast.error(apiErrorMessage(e, "Payment verification failed. No access granted."));
              setLoading(false);
            }
          },
          modal: { ondismiss: () => setLoading(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
        rzp.open();
        return;
      }
      // Stripe (USD)
      const { data } = await api.post("/payments/checkout", {
        course_id: "indian_fashion_illustration",
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Checkout failed"));
    } finally { if (currency !== "inr") setLoading(false); }
  };

  const priceInr = settings?.price_inr ?? 9999;
  const priceUsd = settings?.price_usd ?? 120;
  const price = currency === "inr" ? priceInr : priceUsd;

  if (authLoading || !user) {
    return (
      <div className="bg-[#FAFAFA] min-h-screen">
        <Nav />
        <div className="p-10 text-center text-[#6B6B6B]">Redirecting…</div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA] min-h-screen" data-testid="enroll-page">
      <Nav />
      <section className="max-w-2xl mx-auto px-6 md:px-10 pt-20 md:pt-24 pb-32">
        <p className="overline mb-6"><span className="gold-line mr-3" />Complete your enrolment</p>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl leading-[1.05]">
          One step from the <span className="font-serif-italic italic text-[#D4AF37]">studio</span>.
        </h1>
        <p className="mt-6 text-[#3a3a3a] leading-relaxed max-w-md">
          Hello {user.name?.split(" ")[0]}, choose your currency and complete payment to unlock the course.
        </p>

        <div className="mt-12 border border-black/15 bg-white p-8 md:p-10">
          <p className="overline">Enrollment</p>
          <div className="mt-4 flex items-baseline gap-3">
            <p className="font-serif-display text-2xl text-black/40 line-through">
              {currency === 'inr' ? '₹19,999' : '$240'}
            </p>
            <span className="text-[10px] tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37] px-2 py-1">50% OFF</span>
          </div>
          <p className="font-serif-display text-5xl mt-2" data-testid="enroll-price">
            {currency === 'inr' ? '₹' : '$'}{price.toLocaleString()}
          </p>
          <p className="text-xs mt-2 text-[#D4AF37] italic font-serif-italic">Early Bird Offer — limited seats</p>
          <p className="overline mt-2">One-time · 3 months access</p>

          <div className="mt-8 flex border border-black/15">
            <button
              data-testid="enroll-currency-inr"
              onClick={() => setCurrency('inr')}
              className={`flex-1 py-3 text-xs uppercase tracking-widest ${currency==='inr' ? 'bg-black text-white' : 'text-black'}`}
            >INR ₹</button>
            <button
              data-testid="enroll-currency-usd"
              onClick={() => setCurrency('usd')}
              className={`flex-1 py-3 text-xs uppercase tracking-widest ${currency==='usd' ? 'bg-black text-white' : 'text-black'}`}
            >USD $</button>
          </div>

          <button
            onClick={startPayment}
            disabled={loading}
            data-testid="enroll-pay-button"
            className="btn-editorial w-full mt-6"
          >{loading ? 'Redirecting…' : 'Pay & Enroll'}</button>

          <ul className="mt-8 space-y-3 text-sm text-[#3a3a3a]">
            <li className="flex justify-between border-b border-black/5 pb-2"><span>~2 hours of studio video</span><span className="text-[#D4AF37]">✓</span></li>
            <li className="flex justify-between border-b border-black/5 pb-2"><span>3 months of access</span><span className="text-[#D4AF37]">✓</span></li>
            <li className="flex justify-between border-b border-black/5 pb-2"><span>Downloadable worksheets</span><span className="text-[#D4AF37]">✓</span></li>
            <li className="flex justify-between border-b border-black/5 pb-2"><span>Signed certificate</span><span className="text-[#D4AF37]">✓</span></li>
            <li className="flex justify-between"><span>Instructor reviewed</span><span className="text-[#D4AF37]">✓</span></li>
          </ul>
        </div>
      </section>
      <Footer />
    </div>
  );
}
